import { Identity } from './User'

export interface CrossClientQueryAtomicClause {
  variable: string
  description: string
  questionForm: string
}

export interface CrossClientQueryLogicalQuestionDecomposition {
  explanation: string
  logicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
}

export interface CrossClientQueryLogicalQuestionNNFDecomposition {
  nnfExplanation: string
  nnfLogicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
  negatedClauses: Record<string, string>
}

export interface PredefinedCrossClientQueryCategoryIcon {
  type: 'bootstrap' | 'svgUrl'
  value: string
}

export interface PredefinedCrossClientQueryCategoryMetadata {
  textColor: string
  iconBackgroundColor: string
  iconBorderColor: string
  icon: PredefinedCrossClientQueryCategoryIcon
}

export interface PredefinedCrossClientQueryCategory {
  name: string
  displayName: string
  metadata: PredefinedCrossClientQueryCategoryMetadata
}

export interface PredefinedCrossClientQuery {
  id: string
  category: PredefinedCrossClientQueryCategory
  title: string
  description: string
  question: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition: CrossClientQueryLogicalQuestionNNFDecomposition | null
}

export const CrossClientQueryPhases = {
  NO_CLIENTS_WERE_FOUND: 'noClientsWereFound',
  NO_CLIENTS_AFTER_FILTER_IDS: 'noClientsAfterFilterIDs',
  NO_CLIENTS_AFTER_FILTER_ROLE: 'noClientsAfterFilterRole',
  QUESTION_ERROR: 'questionError',
  INITIALIZED: 'initialized',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  ERRORED: 'errored',
} as const

export type CrossClientQueryPhase = (typeof CrossClientQueryPhases)[keyof typeof CrossClientQueryPhases]

export const CrossClientQueryResultStatuses = {
  INITIALIZED: 'initialized',
  SUCCEEDED: 'succeeded',
  NO_DATA: 'noData',
  FAILED: 'failed',
} as const

export type CrossClientQueryResultStatus = (typeof CrossClientQueryResultStatuses)[keyof typeof CrossClientQueryResultStatuses]

export const CrossClientQueryResultMatches = {
  MATCH: 'full',
  POTENTIAL: 'potential',
  NO_MATCH: 'noMatch',
} as const

export type CrossClientQueryResultMatch = (typeof CrossClientQueryResultMatches)[keyof typeof CrossClientQueryResultMatches]

export const CROSS_CLIENT_MATCH_NONE = 'none'

export const CrossClientQueryEvaluationTypes = {
  LOCAL_RESOLVER: 'localResolver',
  AI: 'ai',
} as const

export type CrossClientQueryEvaluationType = (typeof CrossClientQueryEvaluationTypes)[keyof typeof CrossClientQueryEvaluationTypes]

export const CrossClientQueryEvaluationStatuses = {
  PENDING: 'pending',
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  CANCELING: 'canceling',
  CANCELED: 'canceled',
  ERRORED: 'errored',
} as const

export type CrossClientQueryEvaluationStatus = (typeof CrossClientQueryEvaluationStatuses)[keyof typeof CrossClientQueryEvaluationStatuses]

export const CrossClientQueryEvaluationResultStatuses = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELED: 'canceled',
} as const

export type CrossClientQueryEvaluationResultStatus = (typeof CrossClientQueryEvaluationResultStatuses)[keyof typeof CrossClientQueryEvaluationResultStatuses]

export const CrossClientQueryResolverStages = {
  BULK: 'bulk',
  LOCAL: 'local',
} as const

export type CrossClientQueryResolverStage = (typeof CrossClientQueryResolverStages)[keyof typeof CrossClientQueryResolverStages]

export const CrossClientQueryResultOrderBys = {
  MATCH: 'match',
  UPDATED_AT: 'updatedAt',
  BULK_RESOLVER_SCORE: 'bulkResolverScore',
  LOCAL_RESOLVER_SCORE: 'localResolverScore',
  NEXT_STEP: 'nextStep',
} as const

export type CrossClientQueryResultOrderBy = (typeof CrossClientQueryResultOrderBys)[keyof typeof CrossClientQueryResultOrderBys]

export const CrossClientResultTabs = {
  MATCH: 'match',
  POTENTIAL: 'potential',
  REMAINING: 'remaining',
  NO_MATCH: 'noMatch',
  MISSING_DATA: 'missingData',
} as const

export type CrossClientResultTab = (typeof CrossClientResultTabs)[keyof typeof CrossClientResultTabs]

export const CROSS_CLIENT_RESULT_TAB_ORDER: CrossClientResultTab[] = [
  CrossClientResultTabs.MATCH,
  CrossClientResultTabs.POTENTIAL,
  CrossClientResultTabs.REMAINING,
  CrossClientResultTabs.NO_MATCH,
  CrossClientResultTabs.MISSING_DATA,
]

export interface CrossClientQueryMatchCounts {
  full: number
  potential: number
  noMatch: number
}

export type CrossClientQueryMatchDistribution = Partial<Record<CrossClientQueryResultEvaluationType, CrossClientQueryMatchCounts>>

export const CrossClientQueryResultEvaluationTypes = {
  RESOLVER: 'resolver',
  AI: 'ai',
  LOCAL_RESOLVER: 'localResolver',
} as const

export type CrossClientQueryResultEvaluationType = (typeof CrossClientQueryResultEvaluationTypes)[keyof typeof CrossClientQueryResultEvaluationTypes]

export const CrossClientQueryStopReasons = {
  TOO_BROAD: 'tooBroad',
  TOO_NARROW: 'tooNarrow',
  INSUFFICIENT_FUNDS: 'insufficientFunds',
} as const

export type CrossClientQueryStopReason = (typeof CrossClientQueryStopReasons)[keyof typeof CrossClientQueryStopReasons]

export const CrossClientActionPhases = {
  STARTED: 'started',
  COMPLETED: 'completed',
} as const

export type CrossClientActionPhase = (typeof CrossClientActionPhases)[keyof typeof CrossClientActionPhases]

export interface CrossClientAction {
  id: string
  executionId: string
  clientId: string
  actionType: string
  phase: CrossClientActionPhase
  details: Record<string, unknown> | null
  startedAt: string
  finishedAt: string | null
}

export const CrossClientQueryClientQueryModes = {
  ONLY_OWNED: 'onlyOwned',
  WITH_SHARED: 'withShared',
  ALL_AVAILABLE: 'allAvailable',
} as const

export type CrossClientQueryClientQueryMode = (typeof CrossClientQueryClientQueryModes)[keyof typeof CrossClientQueryClientQueryModes]

interface ClientProfileCrossClientQueryDataSource {
  type: 'clientProfile'
}

interface IntegrationCrossClientQueryDataSource {
  type: 'integration'
  integrationType: string
}

interface MeetingCrossClientQueryDataSource {
  type: 'meeting'
}

export type CrossClientQueryDataSource = ClientProfileCrossClientQueryDataSource | IntegrationCrossClientQueryDataSource | MeetingCrossClientQueryDataSource

export interface CrossClientQueryExecutionEvaluationCounts {
  pending: number
  queued: number
  running: number
  canceling: number
  completed: number
  canceled: number
  errored: number
}

export interface CrossClientQueryExecution {
  id: string
  createdAt: string
  updatedAt: string
  finishedAt: string | null
  canceledAt: string | null
  bulkStartedAt: string | null
  bulkFinishedAt: string | null
  createdBy: Identity
  updatedBy: Identity
  phase: CrossClientQueryPhase
  clientIds: string[]
  resultCount: number
  sources: CrossClientQueryDataSource[]
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  resolvableClauseVariables?: string[] | null
  localResolvableClauseVariables?: string[] | null
  canLocalEvaluateTrue: boolean
  canLocalFulfillClause: boolean
  userQuestion: string
  clientQueryMode?: CrossClientQueryClientQueryMode
  stopReason?: CrossClientQueryStopReason | null
  usageReportedAt?: string | null
  matches?: CrossClientQueryExecutionMatches
  progress?: CrossClientQueryExecutionProgressSummary
  evaluationCounts?: CrossClientQueryExecutionEvaluationCounts
}

export interface CrossClientQueryExecutionMatches {
  full: number
  potential: number
  noMatch: number
  pending: number
  unactioned: number
}

export interface CrossClientQueryResultBucketCount {
  status: CrossClientQueryResultStatus
  match: CrossClientQueryResultMatch | null
  lastResolverStage: CrossClientQueryResolverStage | null
  count: number
}

export interface CrossClientQueryExecutionProgressSummary {
  statuses: Partial<Record<CrossClientQueryResultStatus, number>>
  lastUpdatedAt?: string | null
}

export const getExecutionStartedAt = (execution: CrossClientQueryExecution): string => {
  return execution.bulkStartedAt ?? execution.createdAt
}

export interface CrossClientQueryResolverScore {
  resolverId: string
  score: number
  reasoning: string | null
  parameters: Record<string, unknown> | null
}

export interface CrossClientQueryPropositionResolverScores {
  variable: string
  resolverScores: CrossClientQueryResolverScore[]
}

export interface CrossClientQueryClauseResolverMapping {
  propositionResolverScores: CrossClientQueryPropositionResolverScores[]
}

export interface CrossClientQueryDecompositionResult {
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

export interface CrossClientQueryInitializationResult {
  executionId: string
  phase: CrossClientQueryPhase
  resolvableClauseVariables: string[]
  localResolvableClauseVariables: string[]
}

export interface CrossClientQueryResultSummary {
  fullCount: number
  potentialCount: number
  noMatchCount: number
  pendingCount: number
  totalCount: number
}

export interface CrossClientQueryEvaluationProgress {
  statusCounts: Partial<Record<CrossClientQueryEvaluationResultStatus, number>>
  producedMatchCounts: Partial<Record<CrossClientQueryResultMatch, number>>
}

export interface CrossClientQueryEvaluation {
  id: string
  executionId: string
  type: CrossClientQueryEvaluationType
  status: CrossClientQueryEvaluationStatus
  clientCount: number
  stopReason?: CrossClientQueryStopReason | null
  createdBy: Identity
  createdAt: string
  updatedAt: string
  queuedAt?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  canceledAt?: string | null
  progress?: CrossClientQueryEvaluationProgress
}

export type CrossClientQueryDecision = boolean | null

export interface CrossClientQueryResultSource {
  source: CrossClientQueryDataSource
  retrievedAt: string | null
  metadata?: Record<string, unknown>
}

export interface CrossClientQueryResultAnswer {
  decision: CrossClientQueryDecision
  explanation: string
  source?: CrossClientQueryResultSource
}

export interface CrossClientQueryResult {
  id: string
  clientId: string
  status: CrossClientQueryResultStatus
  createdAt: string
  updatedAt: string
  answers?: CrossClientQueryResultAnswer[]
  bulkResolverAnswers?: CrossClientQueryResultAnswer[] | null
  localResolverAnswers?: CrossClientQueryResultAnswer[] | null
  match?: CrossClientQueryResultMatch | null
  evaluationType?: CrossClientQueryResultEvaluationType
  lastResolverStage?: CrossClientQueryResolverStage | null
  matchSetManuallyAt?: string | null
  activeEvaluationId?: string | null
  concludedByEvaluationId?: string | null
}

export interface EvaluatedCrossClientQueryResult extends CrossClientQueryResult {
  match: CrossClientQueryResultMatch
}

export interface CrossClientQueryExecutionProgressResponse {
  executionId: string
  phase: CrossClientQueryPhase
  status: CrossClientQueryResultStatus
  resultCount: number
  createdAt: string
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}

export interface CrossClientQueryExecutionProgress {
  executionId: string
  phase: CrossClientQueryPhase
  statuses: Record<CrossClientQueryResultStatus, number>
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}
